import { SCOPE_ROOT_SELECTOR } from "../templating/scopeRoot";
import { templateParseRoot } from "../templating/templateParseRoot";

/**
 * Resolve and apply the host-page styling channels into the widget's shadow root.
 *
 * Two sources, both optional, both additive:
 *
 *  1. `stylesheet-url` attribute on `<lastbot-chat>`. The widget fetches the URL via fetch()
 *     and injects the resulting CSS as a `<style>` inside the shadow root.
 *  2. `<style id="lastbot-styles">…</style>` on the host page. The widget reads its textContent
 *     and injects it as a `<style>` inside the shadow root.
 *
 * Order: external URL first (if present), then inline tag. The inline tag wins on conflict
 * because it is appended last.
 *
 * A scope root (`[data-lastbot]` container or the `<lastbot-chat>` element itself) may
 * instead carry an id-less `<template><style>…</style></template>` child (passed via
 * `options.container`). When present it is injected (marked `inline-template`) and
 * REPLACES the global `<style id="lastbot-styles">` for that instance — scope-root-first,
 * global-fallback, all-or-nothing. `stylesheet-url` still applies independently.
 *
 * `<style>` blocks inside `<template>` are stripped by parseTemplate.ts (separate channel).
 */
export const applyHostStyling = async (
  shadow: ShadowRoot,
  options: {
    stylesheetUrl?: string;
    inlineStyleId?: string;
    doc?: Document;
    container?: Element;
  },
): Promise<void> => {
  const doc = options.doc ?? document;

  if (options.stylesheetUrl) {
    await injectExternalStylesheet(shadow, options.stylesheetUrl);
  }

  // Container-first, global-fallback (all-or-nothing): a [data-lastbot] container
  // with its own <template id="lastbot-styles"> replaces the global
  // <style id="lastbot-styles"> for this instance. `stylesheet-url` above is a
  // per-instance channel and is unaffected.
  if (options.container && injectScopedInlineStylesheet(shadow, options.container)) {
    return;
  }

  if (options.inlineStyleId) {
    injectInlineStylesheet(shadow, doc, options.inlineStyleId);
  }
};

const injectExternalStylesheet = async (
  shadow: ShadowRoot,
  url: string,
): Promise<void> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.warn(
        `[lastbot-chat] stylesheet-url "${url}" returned ${response.status}; skipping`,
      );
      return;
    }
    const css = await response.text();
    const styleEl = document.createElement("style");
    styleEl.setAttribute("data-lastbot-source", "stylesheet-url");
    styleEl.textContent = css;
    shadow.appendChild(styleEl);
  } catch (err) {
    console.warn(
      `[lastbot-chat] failed to fetch stylesheet-url "${url}":`,
      err,
    );
  }
};

const injectInlineStylesheet = (
  shadow: ShadowRoot,
  doc: Document,
  id: string,
): void => {
  // The global inline style is a `<style id="lastbot-styles">`. A [data-lastbot] container
  // uses the id-less <template><style> scoped channel instead, so a `#lastbot-styles`
  // element sitting inside a [data-lastbot] belongs to that container (or is a stray) and
  // must NOT be treated as global. (A `<style id="lastbot-styles">` inside a <lastbot-chat>
  // is intentionally kept global — matched by id, document-wide; see docs/api.md
  // § "Scoped to a single widget".) getElementById could also return the wrong node when
  // the id repeats, so filter explicitly to unowned candidates.
  const globalCandidates = Array.from(doc.querySelectorAll(`[id="${id}"]`)).filter(
    (candidate) => candidate.closest("[data-lastbot]") === null,
  );
  const el = globalCandidates.find(
    (candidate) => candidate.tagName.toLowerCase() === "style",
  );
  if (!el) {
    // Preserve the DX warning: a global #id exists but isn't a <style>.
    if (globalCandidates.length > 0) {
      console.warn(
        `[lastbot-chat] "#${id}" exists but is a <${globalCandidates[0].tagName.toLowerCase()}>; expected <style>`,
      );
    }
    return;
  }
  const styleEl = document.createElement("style");
  styleEl.setAttribute("data-lastbot-source", "inline-tag");
  styleEl.textContent = el.textContent ?? "";
  shadow.appendChild(styleEl);
};

/**
 * Read a container's scoped styles block and inject it into the shadow root. The block
 * is an **id-less `<template>` that wraps a `<style>`** — identified by content, not by
 * id, so there are no duplicate `id="lastbot-styles"` across a page:
 *
 *   <div data-lastbot …>
 *     <template><style>:host { … }</style></template>
 *   </div>
 *
 * Returns true when such a block is present so the caller skips the global inline style
 * (all-or-nothing: the container owns styling).
 *
 * Selection rules:
 *  - **Id-less**: the styles block is an `<template>` with no `id` (the canonical form).
 *    Requiring id-less keeps the contract explicit — a `<template id="lastbot-navbar">`
 *    (structural) or an embedder's own `<template id="…">` that happens to contain a
 *    `<style>` is never mistaken for the styles block.
 *  - Ownership: the template's nearest scope root (`[data-lastbot]` or `<lastbot-chat>`)
 *    IS `container` (so it may be wrapped at any depth, and a nested scope root's block
 *    is not picked up here).
 *  - Must contain a `<style>` — that is what marks it as the styles block.
 *
 * A `<template>` wrapper is required (not a bare `<style>`) so nothing renders or leaks
 * onto the host page; its content is inert until read here.
 */
const injectScopedInlineStylesheet = (
  shadow: ShadowRoot,
  container: Element,
): boolean => {
  const tpl = Array.from(
    container.querySelectorAll<HTMLTemplateElement>("template"),
  ).find((t) => {
    if (t.id !== "" || t.closest(SCOPE_ROOT_SELECTOR) !== container) return false;
    // Prefer .content; fall back to host childNodes for React-authored
    // <template><style> (JSX children land on the host — LAS-2116).
    return templateParseRoot(t).querySelector("style") !== null;
  });
  if (!tpl) return false;
  const css = Array.from(templateParseRoot(tpl).querySelectorAll("style"))
    .map((s) => s.textContent ?? "")
    .join("\n");
  const out = document.createElement("style");
  out.setAttribute("data-lastbot-source", "inline-template");
  out.textContent = css;
  shadow.appendChild(out);
  return true;
};
